import React, { useState } from 'react';
import { ABLE_COLORS } from '../../constants';
import { ShoppingTab } from '../Hub/Kitchen';

/**
 * ShoppingWidget - Self-contained shopping list widget
 * 
 * Manages its own local state for:
 * - Input fields (name, qty, category)
 * - Edit state (inline editing)
 * - Sort preference
 * - Show/hide bought items
 * 
 * Receives data and handlers from props (passed through from App.js)
 */

// Unit options for quantity
const UNIT_OPTIONS = [
  { value: '', label: 'units' },
  { value: 'g', label: 'grams' },
  { value: 'kg', label: 'kg' },
  { value: 'ml', label: 'ml' },
  { value: 'L', label: 'litres' },
  { value: 'oz', label: 'oz' },
  { value: 'lb', label: 'lb' },
  { value: 'cups', label: 'cups' },
  { value: 'tbsp', label: 'tbsp' },
  { value: 'tsp', label: 'tsp' }
];

// Category display config
const CATEGORY_CONFIG = {
  protein: { label: 'Protein', color: ABLE_COLORS.state.creative },
  carb: { label: 'Carbs', color: ABLE_COLORS.state.emotional },
  veg: { label: 'Vegetables', color: ABLE_COLORS.state.physical },
  fruit: { label: 'Fruit', color: ABLE_COLORS.warm },
  dairy: { label: 'Dairy', color: ABLE_COLORS.state.social },
  condiment: { label: 'Condiments', color: ABLE_COLORS.state.mental },
  beverage: { label: 'Beverages', color: ABLE_COLORS.feeling[5] },
  other: { label: 'Other', color: ABLE_COLORS.state.rest }
};

// Auto-detect category from item name
function detectCategory(name) {
  const lower = name.toLowerCase();

  const proteins = ['chicken', 'beef', 'pork', 'lamb', 'fish', 'salmon', 'tuna', 'shrimp', 'prawn', 'egg', 'eggs', 'tofu', 'tempeh', 'turkey', 'bacon', 'sausage', 'mince', 'steak', 'ham', 'meatball'];
  if (proteins.some(p => lower.includes(p))) return 'protein';

  const fruits = ['apple', 'banana', 'orange', 'grape', 'strawberry', 'blueberry', 'raspberry', 'mango', 'pineapple', 'peach', 'pear', 'plum', 'cherry', 'kiwi', 'melon', 'watermelon', 'lemon', 'lime', 'grapefruit', 'avocado', 'berry', 'berries', 'fruit'];
  if (fruits.some(f => lower.includes(f))) return 'fruit';

  const veggies = ['carrot', 'broccoli', 'spinach', 'lettuce', 'tomato', 'cucumber', 'pepper', 'capsicum', 'onion', 'garlic', 'potato', 'sweet potato', 'zucchini', 'squash', 'corn', 'pea', 'bean', 'cabbage', 'kale', 'celery', 'mushroom', 'eggplant', 'aubergine', 'asparagus', 'cauliflower', 'leek', 'vegetable', 'veg', 'salad'];
  if (veggies.some(v => lower.includes(v))) return 'veg';

  const dairy = ['milk', 'cheese', 'yogurt', 'yoghurt', 'butter', 'cream', 'sour cream', 'cottage', 'cheddar', 'mozzarella', 'parmesan', 'feta', 'brie', 'ice cream'];
  if (dairy.some(d => lower.includes(d))) return 'dairy';

  const carbs = ['bread', 'rice', 'pasta', 'noodle', 'cereal', 'oat', 'flour', 'tortilla', 'wrap', 'bagel', 'croissant', 'roll', 'bun', 'cracker', 'chip', 'crisp', 'pretzel', 'quinoa', 'couscous'];
  if (carbs.some(c => lower.includes(c))) return 'carb';

  const beverages = ['juice', 'water', 'soda', 'cola', 'coffee', 'tea', 'beer', 'wine', 'vodka', 'whisky', 'rum', 'gin', 'drink', 'smoothie', 'shake'];
  if (beverages.some(b => lower.includes(b))) return 'beverage';

  const condiments = ['sauce', 'ketchup', 'mustard', 'mayo', 'mayonnaise', 'dressing', 'vinegar', 'oil', 'olive oil', 'soy sauce', 'hot sauce', 'salsa', 'jam', 'jelly', 'honey', 'syrup', 'peanut butter', 'nutella', 'spread', 'spice', 'salt', 'pepper', 'herb', 'seasoning'];
  if (condiments.some(c => lower.includes(c))) return 'condiment';

  return 'other';
}

// Parse existing quantity into number and unit
function parseQuantity(qty) {
  if (!qty) return { number: '', unit: '' };
  const match = qty.match(/^([\d.]+)\s*(.*)$/);
  if (match) {
    return { number: match[1], unit: match[2] || '' };
  }
  return { number: qty, unit: '' };
}

export default function ShoppingWidget({
  // Data from App.js
  shoppingList = [],
  boughtItems = [],
  categories = ['protein', 'carb', 'veg', 'fruit', 'dairy', 'condiment', 'beverage', 'other'],
  // Handlers from App.js
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onMarkAsBought,
  onUndoBought,
  // UI
  showToast
}) {
  // ========== LOCAL STATE ==========

  // Rapid entry state
  const [inputName, setInputName] = useState('');
  const [inputQtyNumber, setInputQtyNumber] = useState('');
  const [inputQtyUnit, setInputQtyUnit] = useState('');
  const [inputCategory, setInputCategory] = useState('other');

  // Inline edit state
  const [editingItemId, setEditingItemId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editQtyNumber, setEditQtyNumber] = useState('');
  const [editQtyUnit, setEditQtyUnit] = useState('');
  const [editCategory, setEditCategory] = useState('other');

  // View state
  const [sortBy, setSortBy] = useState('category');
  const [showBought, setShowBought] = useState(false);

  // ========== HANDLERS ==========

  // Handle name input change with auto-category detection
  const handleNameChange = (name) => {
    setInputName(name);
    if (name.trim()) {
      const detected = detectCategory(name);
      setInputCategory(detected);
    }
  };

  // Add item directly to shopping list
  const handleAddItem = () => {
    if (!inputName.trim()) return;

    let quantity = '';
    if (inputQtyNumber) {
      quantity = inputQtyUnit ? `${inputQtyNumber} ${inputQtyUnit}` : inputQtyNumber;
    }

    onAddItem({
      name: inputName.trim(),
      quantity,
      category: inputCategory,
      status: 'needToBuy'
    });

    // Reset form
    setInputName('');
    setInputQtyNumber('');
    setInputQtyUnit('');
    setInputCategory('other');
  };

  // Handle Enter key for rapid entry
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputName.trim()) {
      e.preventDefault();
      handleAddItem();
    }
  };

  // Start editing item
  const startEditingItem = (item) => {
    const parsed = parseQuantity(item.quantity);
    setEditingItemId(item.id);
    setEditName(item.name);
    setEditQtyNumber(parsed.number);
    setEditQtyUnit(parsed.unit);
    setEditCategory(item.category || 'other');
  };

  // Save edited item
  const saveItemEdit = (itemId) => {
    let quantity = '';
    if (editQtyNumber) {
      quantity = editQtyUnit ? `${editQtyNumber} ${editQtyUnit}` : editQtyNumber;
    }
    onUpdateItem(itemId, {
      name: editName.trim() || 'Unknown item',
      quantity,
      category: editCategory
    });
    cancelEdit();
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingItemId(null);
    setEditName('');
    setEditQtyNumber('');
    setEditQtyUnit('');
    setEditCategory('other');
  };

  const handleMarkAsBought = (item) => {
    onMarkAsBought(item.id);
    if (showToast) {
      showToast(`Got ${item.name}!`);
    }
  };

  const handleRemove = (item) => {
    onRemoveItem(item.id);
    if (showToast) {
      showToast(`Removed ${item.name} from list`);
    }
  };

  const handleUndoBought = (item) => {
    onUndoBought(item.id);
    if (showToast) {
      showToast(`${item.name} back on list`);
    }
  };

  // Increment quantity by 1
  const handleIncrement = (item) => {
    const parsed = parseQuantity(item.quantity);
    const currentNum = parseInt(parsed.number, 10) || 0;
    const newNum = currentNum + 1;
    const newQuantity = parsed.unit ? `${newNum} ${parsed.unit}` : `${newNum}`;
    onUpdateItem(item.id, { quantity: newQuantity });
  };

  // Decrement quantity (min 1)
  const handleDecrement = (item) => {
    const parsed = parseQuantity(item.quantity);
    const currentNum = parseInt(parsed.number, 10) || 1;
    if (currentNum <= 1) return;
    const newNum = currentNum - 1;
    const newQuantity = parsed.unit ? `${newNum} ${parsed.unit}` : `${newNum}`;
    onUpdateItem(item.id, { quantity: newQuantity });
  };

  // ========== RENDER ==========

  return (
    <ShoppingTab
      shoppingList={shoppingList}
      boughtItems={boughtItems}
      categories={categories}
      categoryConfig={CATEGORY_CONFIG}
      unitOptions={UNIT_OPTIONS}
      // Rapid entry
      inputName={inputName}
      inputQtyNumber={inputQtyNumber}
      inputQtyUnit={inputQtyUnit}
      inputCategory={inputCategory}
      onInputNameChange={handleNameChange}
      onInputQtyNumberChange={setInputQtyNumber}
      onInputQtyUnitChange={setInputQtyUnit}
      onInputCategoryChange={setInputCategory}
      onAddItem={handleAddItem}
      onKeyDown={handleKeyDown}
      // Sort/view
      sortBy={sortBy}
      onSortByChange={setSortBy}
      showBought={showBought}
      onShowBoughtToggle={() => setShowBought(!showBought)}
      // Edit
      editingItemId={editingItemId}
      editName={editName}
      editQtyNumber={editQtyNumber}
      editQtyUnit={editQtyUnit}
      editCategory={editCategory}
      onEditNameChange={setEditName}
      onEditQtyNumberChange={setEditQtyNumber}
      onEditQtyUnitChange={setEditQtyUnit}
      onEditCategoryChange={setEditCategory}
      onStartEdit={startEditingItem}
      onSaveEdit={saveItemEdit}
      onCancelEdit={cancelEdit}
      // Item actions
      onMarkAsBought={handleMarkAsBought}
      onRemove={handleRemove}
      onUndoBought={handleUndoBought}
      onIncrement={handleIncrement}
      onDecrement={handleDecrement}
    />
  );
}
